import React, { useEffect, useState, useMemo, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { CircularProgress } from "@mui/material";

import "./ImportAdditionalDataForm.scss";

import {
    AbsDropDown,
    AttachmentsUpload,
    Button,
    COLORS,
    Table,
    Modal,
} from "abs-ui-components";

import xlsxImage from "../../../images/excel-file-icon.svg";
import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import webhookQueueService from "../../../services/WebhookQueueService";
import { FeatureType } from "../../../constants/FeatureToggles";
import { ClaimsContext } from "../../../services/ClaimsService";

const ImportAdditionalDataForm = (props) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [invalidFiles, setInvalidFiles] = useState([]);
    const [invalidVoyages, setInvalidVoyages] = useState([]);
    const [invalidBDNs, setInvalidBDNs] = useState([]);
    const [invalidFuelDetails, setInvalidFuelDetails] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [selectedWcn, setSelectedWcn] = useState([]);
    const [wcnOptionList, setWcnOptionList] = useState([]);
    const [uploadResponse, setUploadResponse] = useState([]);

    const [dragged, setDragged] = useState(null);

    const { userHasFeature, userFeaturesAreLoaded } = useContext(ClaimsContext);
    const [currentOperationId, setCurrentOperationId] = useState(null);
    const currentOperationIdRef = useRef(null);
    const [webhookPrompt, setWebhookPrompt] = useState(null);

    useEffect(() => {
        cancelFileSelection();

        // Re attach callbacks for any in progress Additional Import Report after page refresh
        const state = webhookQueueService.getState();
        const inProgressOp = state.queue.find(
            (op) =>
                op.label === "Additional Import Report" &&
                op.status === "processing"
        );
        if (inProgressOp?.webhookId) {
            setCurrentOperationId(inProgressOp.id);
            currentOperationIdRef.current = inProgressOp.id;
            webhookQueueService.reattachCallbacks(inProgressOp.webhookId, {
                onComplete: (result) => {
                    setCurrentOperationId(null);
                    currentOperationIdRef.current = null;
                    props.setDisableBtn(false);

                    if (
                        result?.isSuccess === false ||
                        result?.IsSuccess === false
                    ) {
                        const errorMsg =
                            result?.Data?.ErrorMessage ||
                            result?.data?.errorMessage ||
                            result?.errorMessage ||
                            "Error while importing reports. Please try again";
                        setUploadingFile(false);
                        emitToasterMsg(
                            "error",
                            "Import Reports",
                            errorMsg
                        );
                    } else if (result?.IsSuccess || result?.isSuccess) {
                        setUploadingFile(false);
                        props.cancelImportReport(true);
                        emitToasterMsg(
                            "success",
                            "Import Reports",
                            "Imported Successfully"
                        );
                    } else {
                        setUploadingFile(false);
                        props.cancelImportReport(true);
                        emitToasterMsg(
                            "success",
                            "Import Reports",
                            "Imported Successfully"
                        );
                    }
                },
                onError: (error, statusData) => {
                    setCurrentOperationId(null);
                    currentOperationIdRef.current = null;
                    props.setDisableBtn(false);

                    const result = statusData?.result;
                    const errorMsg =
                        result?.Data?.ErrorMessage ||
                        result?.data?.errorMessage ||
                        result?.ErrorMessage ||
                        (typeof error === "string"
                            ? error
                            : error?.message) ||
                        "Error while importing reports. Please try again";
                    setUploadingFile(false);
                    emitToasterMsg("error", "Import Reports", errorMsg);
                },
            });
        }
    }, []);

    useEffect(() => {
        const unsubscribe = webhookQueueService.subscribe((state) => {
            const waitingOp = state.waitingForInput?.find(
                (op) => op.id === currentOperationIdRef.current
            );
            if (waitingOp?.prompt) {
                setWebhookPrompt({
                    operationId: waitingOp.id,
                    ...waitingOp.prompt,
                });
            } else {
                setWebhookPrompt(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Cancel silent webhook operation when the modal is unmounted
    useEffect(() => {
        return () => {
            const opId = currentOperationIdRef.current;
            if (opId) {
                const isWebhookPanelVisible =
                    userFeaturesAreLoaded &&
                    userHasFeature(FeatureType.ViewWebhookPanel);
                if (!isWebhookPanelVisible) {
                    webhookQueueService.cancelOperation(opId);
                    props.setDisableBtn(false);
                }
            }
        };
    }, [userFeaturesAreLoaded]);

    const invalidFilesColumns = useMemo(
        () => [
            { Header: "File Name", accessor: "fileName" },
            {
                Header: "Message",
                accessor: "message",
                Cell: (row) => {
                    return (
                        <span className="text-danger">{row?.row?.message}</span>
                    );
                },
            },
        ],
        []
    );

    const onDrag = (e) => {
        e.preventDefault();
        if (e.type === "dragover") {
            setDragged(true);
        }
        setDragged((prev) => {
            return prev;
        });
    };

    const onDrop = (e) => {
        e.preventDefault();
        if (e.type === "drop") {
            setDragged(false);
        }
        setDragged((prev) => {
            return prev;
        });
    };

    const cancelFileSelection = () => {
        setSelectedFiles([]);
        setInvalidFiles([]);
        setInvalidBDNs([]);
        setInvalidFuelDetails([]);
        setInvalidVoyages([]);
        setSelectedWcn([]);
        setWcnOptionList([]);
        setUploadingFile(false);
    };

    const handleRemoveFile = (indexToRemove) => {
        setSelectedFiles(
            selectedFiles.filter((file, index) => index !== indexToRemove)
        );
        setSelectedWcn(
            selectedWcn.filter((option, index) => index !== indexToRemove)
        );
        setWcnOptionList(
            wcnOptionList.filter((option, index) => index !== indexToRemove)
        );
        setUploadResponse(
            uploadResponse?.response?.data?.filter(
                (option, index) => index !== indexToRemove
            )
        );
        if (selectedFiles.length === 1) {
            cancelFileSelection();
            setInvalidFiles([]);
        }
    };

    const handleFilesSelected = (e) => {
        const newFiles = [...selectedFiles];
        const newSelectedWcn = [...selectedWcn];
        e?.forEach((selectedFile) => {
            newFiles.push(selectedFile.file);
            newSelectedWcn.push("");
        });
        setSelectedFiles(newFiles);

        setSelectedWcn(newSelectedWcn);
    };

    const createWcnOptions = (data) => {
        let wcnOptionList = [];
        data.wcnMsts?.forEach((item) => {
            const wcnOption = [];
            if (item.wcnNumber && item.companyName) {
                wcnOptionList.push({
                    label: `${item.wcnNumber} - ${item.companyName}`,
                    value: item.wcnMstId,
                });
            }
        });

        setWcnOptionList(wcnOptionList);
    };

    const uploadFiles = () => {
        setUploadingFile(true);
        const formData = new FormData();

        selectedFiles.forEach((file) => {
            formData.append("file", file);
        });
        axiosHttp
            .post(
                `api/Report/AdditionalImportReportFile?reportMstId=${props.reportMstId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            )
            .then((response) => {
                if (response.data) {
                    if (response.data.isFileValid === false) {
                        setInvalidFiles([
                            {
                                ...response.data,
                                message: response.data.errorMessage,
                            },
                        ]);
                    } else if (response.data.isFileValid === true) {
                        setInvalidFiles([]);
                        setUploadResponse([{ ...response.data }]);
                        createWcnOptions(response.data);
                        const invalidRes = getInvalidDataFromResponse(
                            response.data
                        );
                        setInvalidVoyages(invalidRes?.invalidVoyagesList);
                        setInvalidBDNs(invalidRes?.invalidBDNsList);
                        setInvalidFuelDetails(
                            invalidRes?.invalidFuelDetailsList
                        );
                    }
                }
                setUploadingFile(false);
            })
            .catch((error) => {
                setUploadingFile(false);
                emitToasterMsg(
                    "error",
                    "Import Reports",
                    error?.data?.errorMessage ||
                        "Error while uploading reports. Please try again"
                );
            });
    };

    const createSaveFileDataPayload = () => {
        setUploadingFile(true);
        props.setDisableBtn(true);
        const requestSaveFilePayload = uploadResponse?.map((item, index) => {
            delete item["wcnMsts"];
            item["wcnMstId"] = selectedWcn[index];
            return item;
        });

        if (wcnOptionList.length > 0) {
            saveFileData(requestSaveFilePayload);
        } else {
            setUploadingFile(false);
        }
    };

    const saveFileData = (dataToSend) => {
        webhookQueueService
            .enqueue(
                () =>
                    axiosHttp.post(
                        `api/Report/ConfirmAdditionalImportReport?reportMstId=${props.reportMstId}`,
                        dataToSend[0]
                    ),
                {
                    label: "Additional Import Report",
                    description: "Importing additional report data",
                    onComplete: (result) => {
                        setCurrentOperationId(null);
                        currentOperationIdRef.current = null;
                        props.setDisableBtn(false);

                        if (
                            result?.isSuccess === false ||
                            result?.IsSuccess === false
                        ) {
                            const errorMsg =
                                result?.Data?.ErrorMessage ||
                                result?.data?.errorMessage ||
                                result?.errorMessage ||
                                "Error while importing reports. Please try again";
                            setUploadingFile(false);
                            emitToasterMsg(
                                "error",
                                "Import Reports",
                                errorMsg
                            );
                        } else if (result?.IsSuccess || result?.isSuccess) {
                            setUploadingFile(false);
                            props.cancelImportReport(true);
                            emitToasterMsg(
                                "success",
                                "Import Reports",
                                "Imported Successfully"
                            );
                        } else {
                            setUploadingFile(false);
                            props.cancelImportReport(true);
                            emitToasterMsg(
                                "success",
                                "Import Reports",
                                "Imported Successfully"
                            );
                        }
                    },
                    onError: (error, statusData) => {
                        setCurrentOperationId(null);
                        currentOperationIdRef.current = null;
                        props.setDisableBtn(false);

                        const result = statusData?.result;
                        const errorMsg =
                            result?.Data?.ErrorMessage ||
                            result?.data?.errorMessage ||
                            result?.ErrorMessage ||
                            (typeof error === "string"
                                ? error
                                : error?.message) ||
                            "Error while importing reports. Please try again";
                        setUploadingFile(false);
                        emitToasterMsg(
                            "error",
                            "Import Reports",
                            errorMsg
                        );
                    },
                }
            )
            .then((operationId) => {
                setCurrentOperationId(operationId);
                currentOperationIdRef.current = operationId;
            });
    };

    const getInvalidDataFromResponse = (response) => {
        let invalidVoyagesList = [];
        let invalidBDNsList = [];
        let invalidFuelDetailsList = [];
        const voyages = response.voyageDtlReqs;
        const bdns = response.voyageBdnDtlReqs;
        const fuels = response.reportOtherFuelsDtlReqs;
        voyages?.forEach((e) => {
            invalidVoyagesList.push({
                fileName: response.fileName,
                message: e.errorMessage,
            });
        });

        bdns?.forEach((e) => {
            invalidBDNsList.push({
                fileName: response.fileName,
                message: e.errorMessage,
            });
        });

        fuels?.forEach((e) => {
            invalidFuelDetailsList.push({
                fileName: response.fileName,
                message: e.errorMessage,
            });
        });
        return { invalidVoyagesList, invalidBDNsList, invalidFuelDetailsList };
    };

    const handleSelectedWcnChange = (index, e) => {
        let updatedOptions = selectedWcn.map((option, i) => {
            return i === index ? e.value : option;
        });
        setSelectedWcn(updatedOptions);
    };

    const handlePromptResponse = async (confirmed) => {
        if (!webhookPrompt?.operationId) return;
        await webhookQueueService.respondToPrompt(
            webhookPrompt.operationId,
            confirmed
        );
        setWebhookPrompt(null);
    };

    const onDraggingEnd = (e) => {
        setDragged(false);
    };

    return (
        <>
            {webhookPrompt &&
                createPortal(
                    <div className="confirm-backdrop" />,
                    document.body
                )}
            {webhookPrompt &&
                createPortal(
                    <div className="confirm-modal-layer">
                        <Modal
                            isOpen={!!webhookPrompt}
                            padding={"70px"}
                            headerHeight={"50px"}
                            showFooter
                            onCloseClickListener={() =>
                                handlePromptResponse(false)
                            }
                            footer={
                                <div className="d-flex justify-content-end fixed-action-btns p-4">
                                    <Button
                                        className="modal-delete-btn"
                                        onClick={() =>
                                            handlePromptResponse(true)
                                        }
                                        primaryColor={COLORS.RED.DARK}
                                        primaryBackgroundColor={
                                            COLORS.RED.LIGHT
                                        }
                                    >
                                        Confirm
                                    </Button>
                                    <Button
                                        className="modal-cancel-btn ml-2"
                                        onClick={() =>
                                            handlePromptResponse(false)
                                        }
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            }
                        >
                            <div>
                                {webhookPrompt?.Message ||
                                    "The server requires your confirmation to proceed."}
                            </div>
                        </Modal>
                    </div>,
                    document.body
                )}
            <div
                className="import-report-container"
                onDragLeave={(e) => onDraggingEnd(e)}
            >
                <div className="p-4">
                    <div
                        onDragOver={(e) => onDrag(e)}
                        className={dragged ? "drag-drop-cls" : null}
                        onDrop={(e) => onDrop(e)}
                    >
                        <AttachmentsUpload
                            onFilesSelected={handleFilesSelected}
                            supportedFormat="excel"
                            supportedFileTypes="xlsx, xls"
                        />
                    </div>

                    <div className="selected-content mt-4">
                        {selectedFiles && selectedFiles.length > 0 && (
                            <>
                                <div className="file-info p-3">
                                    {selectedFiles.map((file, index) => (
                                        <div
                                            key={`import-${index}`}
                                            className="d-flex justify-content-between align-items-center px-3 py-1"
                                        >
                                            <div className="d-flex">
                                                <img
                                                    src={xlsxImage}
                                                    alt="Excel File Icon"
                                                    className="xl-img mt-1"
                                                />
                                                <span className="file-name p-2">
                                                    {" "}
                                                    {file.name}{" "}
                                                </span>
                                            </div>
                                            <div className="file-info-actions">
                                                {wcnOptionList.length > 0 && (
                                                    <AbsDropDown
                                                        options={wcnOptionList}
                                                        value={
                                                            selectedWcn[index]
                                                        }
                                                        selected={
                                                            selectedWcn[index]
                                                        }
                                                        handleChange={(e) =>
                                                            handleSelectedWcnChange(
                                                                index,
                                                                e
                                                            )
                                                        }
                                                        placeholder="Please Select WCN"
                                                    />
                                                )}
                                                <button
                                                    type="button"
                                                    className="close p-0"
                                                    aria-label="Close"
                                                    onClick={() =>
                                                        handleRemoveFile(index)
                                                    }
                                                    disabled={uploadingFile}
                                                >
                                                    <span aria-hidden="true">
                                                        {" "}
                                                        ×{" "}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-4 d-flex justify-content-end upload-container items-center">
                                    {uploadingFile && (
                                        <CircularProgress size={30} />
                                    )}
                                    {wcnOptionList?.length > 0 ? (
                                        <Button
                                            primaryBackgroundColor="#DC1E34"
                                            primaryColor="#DC1E34"
                                            className="p-0 upload-excel ml-4"
                                            onClick={createSaveFileDataPayload}
                                            disabled={
                                                props.disableBtn ||
                                                selectedWcn.some(
                                                    (e) => e === ""
                                                )
                                            }
                                        >
                                            Proceed
                                        </Button>
                                    ) : (
                                        <Button
                                            primaryBackgroundColor="#DC1E34"
                                            primaryColor="#DC1E34"
                                            className="p-0 upload-excel ml-4"
                                            onClick={uploadFiles}
                                            disabled={uploadingFile}
                                        >
                                            Upload
                                        </Button>
                                    )}
                                    <Button
                                        onClick={cancelFileSelection}
                                        className="ml-2 select-file-color cancel-excel"
                                        primaryBackgroundColor="#f9f9f9"
                                        primaryColor="#f9f9f9"
                                        disabled={uploadingFile}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>

                    {invalidFiles?.length > 0 && (
                        <>
                            <div className="mt-3">
                                One or More errors found with files as below :
                            </div>
                            <Table
                                columns={invalidFilesColumns}
                                data={invalidFiles}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ImportAdditionalDataForm;
